import React from 'react';
import {
  FileEdit,
  ScanLine,
  ShieldCheck,
  Trophy,
  Sparkles,
} from 'lucide-react';

/**
 * "How It Works" — HORIZONTAL PIPELINE with a living flow animation.
 *
 * Desktop (lg+): the four steps sit in a row like stations on a
 * conveyor. A track runs through the badge centers carrying:
 *   • a marching-dash layer (the "current" always moving right)
 *   • a glowing dot that travels the full track every 6s
 *   • a pulse ring on each badge timed to the dot's arrival,
 *     so the flow visibly "hits" each station in order
 *
 * Mobile/tablet: the same stations stack vertically and the flow
 * runs downward along a vertical track — same dash + dot language,
 * no breakpoint-specific surprises.
 *
 * All motion is CSS-only and fully disabled under
 * `prefers-reduced-motion` (static line remains, so the connection
 * between steps never disappears).
 */
export default function HowItWorks() {
  const steps = [
    {
      n: '01',
      icon: FileEdit,
      title: 'Open the role',
      detail:
        'Type, paste, or dictate your job description. The AI extracts skills, sets the experience band, and builds screening questions.',
      chips: ['Voice JD', 'AI generator'],
      accent: 'blue',
    },
    {
      n: '02',
      icon: ScanLine,
      title: 'AI screens everyone',
      detail:
        'Upload one resume or a thousand. Every candidate is scored against the role, ranked by fit, and routed to the right assessment.',
      chips: ['AI scoring', 'Auto-ranking'],
      accent: 'emerald',
    },
    {
      n: '03',
      icon: ShieldCheck,
      title: 'Assess at AI speed',
      detail:
        'AI video interviews, proctored coding and behavioural tests run in parallel. Suspicious submissions get flagged and verified.',
      chips: ['AI interview', 'Proctoring'],
      accent: 'violet',
    },
    {
      n: '04',
      icon: Trophy,
      title: 'Hire with confidence',
      detail:
        'Review the AI-ranked shortlist with full evidence, run the final round in built-in rooms, and extend the offer — all in one place.',
      chips: ['Shortlists', 'Offer letter'],
      accent: 'amber',
    },
  ];

  return (
    <section
      className="relative py-14 lg:py-20 overflow-hidden text-white"
      style={{ backgroundColor: 'hsl(164 33% 7%)' }}
    >
      {/* Emerald glow accents on the dark ink */}
      <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-1/4 h-1/3 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
      {/* Subtle grid texture, faded at the edges */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage:
            'radial-gradient(ellipse at center, black 30%, transparent 78%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 30%, transparent 78%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ─── Heading ─── */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
            The workflow
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4 leading-tight">
            From open role to signed offer,
            <br />
            <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-teal-300 bg-clip-text text-transparent">
              in four steps.
            </span>
          </h2>
          <p className="text-lg text-white/70 leading-relaxed">
            Your recruiters and our AI agents — moving every candidate
            forward, from the first resume to the signed letter.
          </p>
        </div>

        {/* ═════════ DESKTOP — HORIZONTAL PIPELINE ═════════ */}
        <div className="hidden lg:block relative">
          {/* Track through the badge centers (badges are h-24 → 48px).
              Spans first-badge center (12.5%) to last-badge center. */}
          <div className="absolute top-12 left-[12.5%] right-[12.5%] -translate-y-1/2 h-1 pointer-events-none">
            {/* Base line */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/10 via-emerald-400/30 to-white/10" />
            {/* Marching dashes — the current, always flowing right */}
            <div className="absolute inset-0 rounded-full flow-dash" />
            {/* Travelling glow dot */}
            <span className="flow-dot" />
          </div>

          <div className="grid grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <StepColumn key={step.n} step={step} index={idx} />
            ))}
          </div>
        </div>

        {/* ═════════ MOBILE / TABLET — VERTICAL PIPELINE ═════════ */}
        <div className="lg:hidden relative max-w-xl mx-auto">
          {/* Vertical track through the badge centers (badges w-16 → 32px) */}
          <div className="absolute left-8 top-8 bottom-8 -translate-x-1/2 w-1 pointer-events-none">
            <div className="absolute inset-0 rounded-full bg-gradient-to-b from-white/10 via-emerald-400/30 to-white/10" />
            <div className="absolute inset-0 rounded-full flow-dash-v" />
            <span className="flow-dot-v" />
          </div>

          <div className="space-y-10">
            {steps.map((step, idx) => (
              <StepRowMobile key={step.n} step={step} index={idx} />
            ))}
          </div>
        </div>
      </div>

      {/* ═════════ FLOW KEYFRAMES ═════════ */}
      <style>{`
        /* Marching dashes — horizontal */
        .flow-dash {
          background-image: linear-gradient(
            to right,
            rgba(52, 211, 153, 0.65) 0 10px,
            transparent 10px 24px
          );
          background-size: 24px 100%;
          animation: dash-flow 1.1s linear infinite;
        }
        @keyframes dash-flow {
          from { background-position: 0 0; }
          to   { background-position: 24px 0; }
        }

        /* Marching dashes — vertical */
        .flow-dash-v {
          background-image: linear-gradient(
            to bottom,
            rgba(52, 211, 153, 0.65) 0 10px,
            transparent 10px 24px
          );
          background-size: 100% 24px;
          animation: dash-flow-v 1.1s linear infinite;
        }
        @keyframes dash-flow-v {
          from { background-position: 0 0; }
          to   { background-position: 0 24px; }
        }

        /* Travelling glow dot — horizontal */
        .flow-dot {
          position: absolute;
          top: 50%;
          left: 0;
          width: 10px;
          height: 10px;
          border-radius: 9999px;
          background: #34d399;
          box-shadow: 0 0 16px 4px rgba(52, 211, 153, 0.55);
          transform: translate(-50%, -50%);
          animation: dot-travel 6s linear infinite;
        }
        @keyframes dot-travel {
          0%   { left: 0%;   opacity: 0; }
          4%   { opacity: 1; }
          96%  { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }

        /* Travelling glow dot — vertical */
        .flow-dot-v {
          position: absolute;
          left: 50%;
          top: 0;
          width: 10px;
          height: 10px;
          border-radius: 9999px;
          background: #34d399;
          box-shadow: 0 0 16px 4px rgba(52, 211, 153, 0.55);
          transform: translate(-50%, -50%);
          animation: dot-travel-v 6s linear infinite;
        }
        @keyframes dot-travel-v {
          0%   { top: 0%;   opacity: 0; }
          4%   { opacity: 1; }
          96%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }

        /* Station pulse — fires as the dot reaches each badge.
           Delays are set inline per badge (0s / 2s / 4s / 6s on a
           6s cycle, matching the dot's pace across 3 segments). */
        @keyframes station-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.5); }
          12%  { box-shadow: 0 0 0 16px rgba(52, 211, 153, 0); }
          100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
        }
        .station-pulse { animation: station-pulse 6s ease-out infinite; }

        @media (prefers-reduced-motion: reduce) {
          .flow-dash, .flow-dash-v { animation: none; }
          .flow-dot, .flow-dot-v { display: none; }
          .station-pulse { animation: none; }
        }
      `}</style>
    </section>
  );
}

/* Per-step accent palettes — keeps the row visually varied without
   leaving the brand family. */
const accentMap = {
  blue: {
    iconBg: 'bg-blue-500/15',
    iconBorder: 'border-blue-400/30',
    iconColor: 'text-blue-300',
  },
  emerald: {
    iconBg: 'bg-emerald-500/15',
    iconBorder: 'border-emerald-400/30',
    iconColor: 'text-emerald-300',
  },
  violet: {
    iconBg: 'bg-violet-500/15',
    iconBorder: 'border-violet-400/30',
    iconColor: 'text-violet-300',
  },
  amber: {
    iconBg: 'bg-amber-500/15',
    iconBorder: 'border-amber-400/30',
    iconColor: 'text-amber-300',
  },
};

/* ─────────────────────────────────────────────────────────────── */
/*  StepColumn — desktop station: badge on the track, content below */
/* ─────────────────────────────────────────────────────────────── */
function StepColumn({ step, index }) {
  const Icon = step.icon;
  const accent = accentMap[step.accent];

  return (
    <div className="group relative text-center">
      {/* Badge — solid surface so the track passes "behind" it */}
      <div
        className="station-pulse relative z-10 mx-auto w-24 h-24 rounded-3xl bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-600 text-emerald-950 flex flex-col items-center justify-center ring-1 ring-white/25 shadow-lg shadow-emerald-500/30 group-hover:scale-105 group-hover:shadow-xl group-hover:shadow-emerald-500/50 transition-all duration-300"
        style={{ animationDelay: `${index * 2}s` }}
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-60">
          Step
        </span>
        <span className="text-3xl font-black leading-none mt-0.5">
          {step.n}
        </span>
      </div>

      {/* Content */}
      <div className="mt-6">
        <div className="flex items-center justify-center gap-2 mb-2.5">
          <div
            className={`w-9 h-9 rounded-lg ${accent.iconBg} border ${accent.iconBorder} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
          >
            <Icon className={`w-[18px] h-[18px] ${accent.iconColor}`} />
          </div>
          <h3 className="text-lg font-bold text-white leading-tight">
            {step.title}
          </h3>
        </div>

        <p className="text-sm text-white/65 leading-relaxed mb-3.5 px-1">
          {step.detail}
        </p>

        <div className="flex flex-wrap justify-center gap-1.5">
          {step.chips.map((chip) => (
            <span
              key={chip}
              className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/5 border border-white/15 text-white/60 hover:border-emerald-400/40 hover:text-emerald-300 transition-colors"
            >
              {chip}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  StepRowMobile — vertical station: badge left, content right     */
/* ─────────────────────────────────────────────────────────────── */
function StepRowMobile({ step, index }) {
  const Icon = step.icon;
  const accent = accentMap[step.accent];

  return (
    <div className="relative flex gap-5">
      {/* Badge — solid surface over the vertical track */}
      <div
        className="station-pulse relative z-10 shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-600 text-emerald-950 flex flex-col items-center justify-center ring-1 ring-white/25 shadow-lg shadow-emerald-500/30"
        style={{ animationDelay: `${index * 2}s` }}
      >
        <span className="text-[8px] font-bold uppercase tracking-[0.15em] opacity-60">
          Step
        </span>
        <span className="text-xl font-black leading-none mt-0.5">
          {step.n}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 mb-1.5">
          <div
            className={`w-8 h-8 rounded-lg ${accent.iconBg} border ${accent.iconBorder} flex items-center justify-center`}
          >
            <Icon className={`w-4 h-4 ${accent.iconColor}`} />
          </div>
          <h3 className="text-base font-bold text-white leading-tight">
            {step.title}
          </h3>
        </div>

        <p className="text-sm text-white/65 leading-relaxed mb-2.5">
          {step.detail}
        </p>

        <div className="flex flex-wrap gap-1.5">
          {step.chips.map((chip) => (
            <span
              key={chip}
              className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/15 text-white/60"
            >
              {chip}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
